<template>
    <AppBreadcrumbs
        :base-url="runtimeConfig.public.BASE_URL"
        :parents="[
            {
                name: config.brand_name,
                full_slug: '',
            },
            {
                name: 'Authors',
                full_slug: 'authors',
            },
        ]"
        :current="{
            name: story.name,
            full_slug: story.full_slug,
        }"
    />
    <div class="author-page">
        <div class="author-page__information">
            <div class="author-page__content">
                <h1 class="author-page__full-name">
                    {{ story.content.first_name }} <span>{{ story.content.last_name }}</span>
                </h1>

                <p
                    v-if="story.content.job_title"
                    class="author-page__job-title"
                >
                    {{ story.content.job_title }}
                </p>

                <p class="author-page__description">
                    {{ story.content.description }}
                </p>

                <ul
                    v-if="socialLinks.length"
                    class="author-page__socials"
                >
                    <li
                        v-for="link in socialLinks"
                        :key="link._uid"
                    >
                        <a
                            class="author-page__socials-link"
                            :href="link.url"
                            target="_blank"
                            rel="me noopener"
                        >
                            {{ link.platform }}
                        </a>
                    </li>
                </ul>
            </div>

            <div class="author-page__image-wrapper">
                <NuxtImg
                    width="300"
                    height="300"
                    fit="cover"
                    format="auto"
                    :src="story.content.image.filename"
                    :alt="story.content.image.alt"
                    class="author-page__image"
                />
            </div>
        </div>

        <div class="author-page__articles">
            <h2 class="author-page__articles-title">
                Articles <span>({{ total }})</span>
            </h2>

            <div class="author-page__articles-grid">
                <KTeaserCard
                    v-for="article in articles"
                    :key="article.uuid"
                    :as="NuxtLink"
                    :to="`/${article.full_slug}`"
                    :text="article.content.subtitle"
                    variant="editorial"
                    class="featured-article-card"
                >
                    <template #media>
                        <KFrame ratio="89 / 40">
                            <NuxtImg
                                :src="article.content.image.filename"
                                :alt="article.content.image.alt"
                                width="712"
                                height="320"
                                fit="cover"
                                format="auto"
                                loading="lazy"
                                sizes="xs:100vw sm:712px md:433px lg:300px"
                            />
                        </KFrame>
                    </template>
                    <template #title>
                        <span v-html="renderHighlightedText(article.content.title)" />
                    </template>
                    <template #meta>
                        <time :datetime="isoDate(article.first_published_at || article.published_at)">
                            {{ formattedDate(article.first_published_at || article.published_at) }}
                        </time>
                    </template>
                </KTeaserCard>
            </div>
        </div>

        <nav
            v-if="totalPages > 1"
            aria-label="Pagination"
        >
            <ul class="author-page__pagination">
                <li>
                    <NuxtLink
                        v-if="page > 1"
                        class="author-page__pagination-item author-page__pagination-item--wide"
                        :to="paginatedPath(page - 1)"
                    >
                        ‹ prev
                    </NuxtLink>
                    <span
                        v-else
                        class="author-page__pagination-item author-page__pagination-item--wide author-page__pagination-item--disabled"
                    >
                        ‹ prev
                    </span>
                </li>
                <li
                    v-for="(item, index) in paginationItems"
                    :key="index"
                >
                    <span
                        v-if="item === '…'"
                        class="author-page__pagination-item author-page__pagination-item--ellipsis"
                        aria-hidden="true"
                    >…</span>
                    <NuxtLink
                        v-else
                        class="author-page__pagination-item"
                        :class="{
                            'author-page__pagination-item--active': item === page,
                        }"
                        :to="paginatedPath(item)"
                        :aria-current="item === page ? 'page' : undefined"
                    >
                        {{ item }}
                    </NuxtLink>
                </li>
                <li>
                    <NuxtLink
                        v-if="page < totalPages"
                        class="author-page__pagination-item author-page__pagination-item--wide"
                        :to="paginatedPath(page + 1)"
                    >
                        next ›
                    </NuxtLink>
                    <span
                        v-else
                        class="author-page__pagination-item author-page__pagination-item--wide author-page__pagination-item--disabled"
                    >
                        next ›
                    </span>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script setup>
import { KFrame, KTeaserCard } from '@vemedia/karibu'
import { DateTime } from 'luxon'
import { resolveComponent } from 'vue'
import { AUTHOR_ARTICLES_PER_PAGE } from '#shared/author-articles'
import renderHighlightedText from '~/helpers/renderHighlightedText'
import '~/components/featured-articles/featured-article-card.scss'
import './author-page.scss'

// Rendered by both /authors/<author> and /authors/<author>/page/<n>; the page number
// is the only difference between them. The fetching lives here rather than in a
// composable because only <script setup> gets Vue's withAsyncContext transform, and
// that is what keeps the Nuxt instance reachable by useAsyncData/useHead after an await.
const props = defineProps({
    author: { type: String, required: true },
    page: { type: Number, default: 1 },
})

const NuxtLink = resolveComponent('NuxtLink')

const formattedDate = date => DateTime.fromISO(date).toFormat('d LLLL yyyy')
const isoDate = date => DateTime.fromISO(date).toISO()

const config = useState('config')
const runtimeConfig = useRuntimeConfig()

const { data: story, error: storyError } = await useAsyncData(`author-${props.author}`, () => $fetch(`/api/page?full_slug=authors/${props.author}`))

if (storyError.value || !story.value) {
    throw createError({ statusCode: 404, statusMessage: 'Author not found', fatal: true })
}

const { data: articlesData } = await useAsyncData(`author-articles-${props.author}-page-${props.page}`, () => $fetch(`/api/author-articles?author=${story.value.uuid}&page=${props.page}`))

const articles = computed(() => articlesData.value?.stories ?? [])
const total = computed(() => articlesData.value?.total ?? 0)
const totalPages = computed(() => Math.ceil(total.value / AUTHOR_ARTICLES_PER_PAGE))

// Truncated pagination: ±2 around the current page with the first and last page
// always visible, '…' marking the gaps. Archives of 7 pages or fewer render in full.
const paginationItems = computed(() => {
    const last = totalPages.value

    if (last <= 7) {
        return Array.from({ length: last }, (_, index) => index + 1)
    }

    const pages = new Set([1, last])
    for (let i = props.page - 2; i <= props.page + 2; i++) {
        if (i > 1 && i < last) {
            pages.add(i)
        }
    }

    const items = []
    let previous = 0
    for (const pageNumber of [...pages].sort((a, b) => a - b)) {
        if (pageNumber - previous > 1) {
            items.push('…')
        }
        items.push(pageNumber)
        previous = pageNumber
    }
    return items
})

// Page 1 always renders, even for an author without articles. A paginated page with
// nothing on it is past the end of the archive and is not a real URL.
if (props.page > 1 && articles.value.length === 0) {
    throw createError({ statusCode: 404, statusMessage: 'No articles found', fatal: true })
}

const basePath = `/${story.value.full_slug}`
const paginatedPath = index => (index === 1 ? basePath : `${basePath}/page/${index}`)

const content = story.value.content
// Names are trimmed: CMS values can carry stray whitespace (e.g. "WeAreTanzania ").
const fullName = `${(content.first_name || '').trim()} ${(content.last_name || '').trim()}`.trim()

// The bio is multi-line plain text; meta descriptions need a single line.
const plainBio = (content.description || '').replace(/\s+/g, ' ').trim()

function truncateAtWord(text, max = 150) {
    if (text.length <= max) {
        return text
    }
    return `${text.slice(0, max).replace(/\s+\S*$/, '')}…`
}

// Optional CMS override wins, otherwise the bio, otherwise a per-author generic
// fallback (some authors have an empty bio, e.g. team accounts).
const baseDescription = content.meta_description
    || (plainBio ? truncateAtWord(plainBio) : `Articles and travel guides by ${fullName} for ${config.value?.brand_name}.`)

// Paginated pages share the base description with a suffix, so every page's
// description is unique.
const metaDescription = props.page === 1
    ? baseDescription
    : `${baseDescription} — page ${props.page} of ${totalPages.value}`

// meta_title only overrides page 1; paginated titles stay computed so they are
// unique. The layout's titleTemplate appends the brand name.
const title = props.page === 1
    ? content.meta_title || `${fullName} — Articles & travel guides`
    : `${fullName} — Articles, page ${props.page} of ${totalPages.value}`

const canonicalUrl = `${runtimeConfig.public.BASE_URL}${paginatedPath(props.page)}`
const authorUrl = `${runtimeConfig.public.BASE_URL}/${story.value.full_slug}`
const ogImage = `${content.image.filename}/m/1200x630`

// Optional social-link bloks; absent on spaces without the schema fields.
const socialLinks = computed(() =>
    (Array.isArray(content.social_links) ? content.social_links : [])
        .filter(link => link.url && link.platform))

// The canonical itself comes from nuxt-seo-utils; only the pagination links are ours.
const links = []

if (props.page > 1) {
    links.push({
        rel: 'prev',
        href: `${runtimeConfig.public.BASE_URL}${paginatedPath(props.page - 1)}`,
    })
}

if (props.page < totalPages.value) {
    links.push({
        rel: 'next',
        href: `${runtimeConfig.public.BASE_URL}${paginatedPath(props.page + 1)}`,
    })
}

useHead({
    title,
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: metaDescription,
        },
        {
            property: 'og:type',
            content: 'profile',
        },
        {
            property: 'profile:first_name',
            content: content.first_name,
        },
        {
            property: 'profile:last_name',
            content: content.last_name,
        },
        {
            property: 'og:url',
            content: canonicalUrl,
        },
        {
            property: 'og:title',
            content: title,
        },
        {
            property: 'og:description',
            content: metaDescription,
        },
        {
            property: 'og:image',
            content: ogImage,
        },
        {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
        },
        {
            hid: 'twitter:domain',
            name: 'twitter:domain',
            content: runtimeConfig.public.BASE_URL,
        },
        {
            hid: 'twitter:url',
            name: 'twitter:url',
            content: canonicalUrl,
        },
        {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: title,
        },
        {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: metaDescription,
        },
        {
            hid: 'twitter:image',
            name: 'twitter:image',
            content: ogImage,
        },
    ],
    link: links,
})

// ProfilePage structured data on page 1 only — the profile is not the main entity
// of a paginated article listing. Person.url matches the author.url that articles
// emit in their own JSON-LD, closing the article ↔ profile entity loop.
// Emitted via raw useHead like the article/tour content types — useSchemaOrg is
// not usable: the installed unhead v3 is incompatible with nuxt-schema-org's
// plugin (@unhead/schema-org peers on unhead v2), so the graph renders empty.
if (props.page === 1) {
    useHead({
        script: [
            {
                hid: 'structured-data',
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ProfilePage',
                    'dateCreated': story.value.first_published_at,
                    'dateModified': story.value.published_at,
                    'mainEntity': {
                        '@type': 'Person',
                        'name': fullName,
                        'givenName': content.first_name,
                        'familyName': content.last_name,
                        'description': plainBio,
                        'image': content.image.filename,
                        'url': authorUrl,
                        'worksFor': {
                            '@type': 'Organization',
                            'name': config.value?.schema_name || config.value?.brand_name,
                            'url': runtimeConfig.public.BASE_URL,
                        },
                        // Optional E-E-A-T fields; simply absent where the schema
                        // or values are missing.
                        ...(content.job_title && { jobTitle: content.job_title }),
                        ...(socialLinks.value.length && { sameAs: socialLinks.value.map(link => link.url) }),
                    },
                }),
            },
        ],
    })
}
</script>
